/**
 * useSimulationStatus Hook
 *
 * Real-time Firestore listener that tracks the most recent
 * Digital Twin simulation status for a given project.
 *
 * Listens to: users/{userId}/projects/{projectId}/twin_simulations
 * Ordered by started_at desc, limit 1 (most recent simulation).
 *
 * Usage:
 *   const { activeSimulation, vmIp, simStatus, loading, error } =
 *     useSimulationStatus({ userId, projectId });
 */

import { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import {
    getFirestore,
    collection,
    query,
    orderBy,
    limit,
    onSnapshot,
    doc,
    updateDoc,
    serverTimestamp,
} from 'firebase/firestore';

const FIRESTORE_DATABASE_ID = 'vortx-flux-site-dna';

/**
 * Lazy-get Firestore using the already-initialized Firebase app.
 * Matches the pattern used in activityService.js.
 */
function getDb() {
    return getFirestore(getApp(), FIRESTORE_DATABASE_ID);
}

/**
 * Convert Firestore timestamp to ISO string for safe serialization.
 * Handles both Firestore Timestamp objects and plain ISO strings.
 */
function normalizeTimestamp(value) {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (value.toDate && typeof value.toDate === 'function') {
        return value.toDate().toISOString();
    }
    if (value.seconds) {
        return new Date(value.seconds * 1000).toISOString();
    }
    return null;
}

/**
 * @param {Object} params
 * @param {string} params.userId - Firebase user ID
 * @param {string} params.projectId - Unified project ID
 * @returns {Object} Simulation status state
 */
export function useSimulationStatus({ userId, projectId }) {
    const [activeSimulation, setActiveSimulation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Cannot subscribe without both IDs
        if (!userId || !projectId) {
            setActiveSimulation(null);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        let unsubscribe;
        try {
            const db = getDb();
            const simCollectionRef = collection(
                db,
                'users',
                userId,
                'projects',
                projectId,
                'twin_simulations'
            );

            const simQuery = query(
                simCollectionRef,
                orderBy('started_at', 'desc'),
                limit(1)
            );

            unsubscribe = onSnapshot(
                simQuery,
                (snapshot) => {
                    if (snapshot.empty) {
                        setActiveSimulation(null);
                    } else {
                        const doc = snapshot.docs[0];
                        const data = doc.data();
                        setActiveSimulation({
                            sim_id: doc.id,
                            status: data.status,
                            vm_ip: data.vm_ip,
                            scenario_id: data.scenario_id,
                            started_at: normalizeTimestamp(data.started_at),
                            stopped_at: normalizeTimestamp(data.stopped_at),
                            updated_at: normalizeTimestamp(data.updated_at),
                            config: data.config,
                            checkpoint: data.checkpoint,
                            user_id: data.user_id,
                        });
                    }
                    setLoading(false);
                    setError(null);
                },
                (err) => {
                    console.error(
                        '[useSimulationStatus] Firestore listener error:',
                        err
                    );
                    setError(err);
                    setLoading(false);
                }
            );
        } catch (err) {
            console.error(
                '[useSimulationStatus] Failed to set up listener:',
                err
            );
            setError(err);
            setLoading(false);
        }

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [userId, projectId]);

    // Derived convenience fields
    const vmIp = activeSimulation?.vm_ip ?? null;
    const simStatus = activeSimulation?.status ?? null;

    return {
        activeSimulation,
        vmIp,
        simStatus,
        loading,
        error,
    };
}

/**
 * useSimulationRuns - Real-time list of recent simulation runs.
 *
 * Listens to: users/{userId}/projects/{projectId}/twin_simulations
 * Ordered by started_at desc, limit 20.
 *
 * @param {Object} params
 * @param {string} params.userId
 * @param {string} params.projectId
 * @returns {{ runs: Array, loading: boolean, error: Error|null }}
 */
export function useSimulationRuns({ userId, projectId }) {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!userId || !projectId) {
            setRuns([]);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        let unsubscribe;
        try {
            const db = getDb();
            const simCollectionRef = collection(
                db,
                'users',
                userId,
                'projects',
                projectId,
                'twin_simulations'
            );

            const runsQuery = query(
                simCollectionRef,
                orderBy('started_at', 'desc'),
                limit(20)
            );

            unsubscribe = onSnapshot(
                runsQuery,
                (snapshot) => {
                    const results = snapshot.docs.map((d) => {
                        const data = d.data();
                        // Skip killed runs — they flash back if not filtered
                        if (data.status === 'killed') return null;
                        return {
                            sim_id: d.id,
                            status: data.status,
                            vm_ip: data.vm_ip,
                            started_at: normalizeTimestamp(data.started_at),
                            stopped_at: normalizeTimestamp(data.stopped_at),
                            config: data.config,
                            scenario_ids: data.scenario_ids,
                            checkpoint: data.checkpoint,
                        };
                    }).filter(Boolean);
                    setRuns(results);
                    setLoading(false);
                    setError(null);
                },
                (err) => {
                    console.error('[useSimulationRuns] Firestore listener error:', err);
                    setError(err);
                    setLoading(false);
                }
            );
        } catch (err) {
            console.error('[useSimulationRuns] Failed to set up listener:', err);
            setError(err);
            setLoading(false);
        }

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [userId, projectId]);

    return { runs, loading, error };
}

/**
 * killSimulation - Mark a simulation as killed in Firestore.
 *
 * Updates the twin_simulations doc with status='killed' and stopped_at=now.
 * The VM will stop on its own via duration limit or be cleaned up on next /start.
 *
 * @param {Object} params
 * @param {string} params.userId
 * @param {string} params.projectId
 * @param {string} params.simId
 */
export async function killSimulation({ userId, projectId, simId }) {
    const db = getDb();
    const simDocRef = doc(
        db,
        'users',
        userId,
        'projects',
        projectId,
        'twin_simulations',
        simId
    );
    await updateDoc(simDocRef, {
        status: 'killed',
        stopped_at: serverTimestamp(),
    });
}
